import { Avatar, Divider, IconButton, MenuList, Tooltip } from '@mui/material';
import React from 'react';

import { PopperSubmenuContainer } from '../../Components/PopperSubmenuContainer';
import { CAPITAL_FOUNDATION_COLORS, NAV_COLOR_PALETTE, tooltipOffsetProps } from '../../Components/styleConstants';
import { delayedMobileCloseClickHandler } from '../../Components/utils';

import { DATA_OG, DATA_TEST } from '../../enums';
import { BadgeVariant, IProfileSettingsOptions } from '../../types';

import { MobileUtilityTray } from '../Mobile/MobileUtilityTray';

import { BadgeNotification, getBadgeAccessibilitySuffix } from './BadgeNotification';
import { TrayMenuItem } from './TrayMenuItem';

const iconButtonStyles = {
  color: NAV_COLOR_PALETTE.secondaryGray,
  width: 32,
  height: 32,
  borderRadius: '50%',
  '&:after': {
    content: '""',
    position: 'absolute',
    top: '-4px',
    bottom: '-4px',
    left: '-4px',
    right: '-4px'
  }
};

export const getTopAndRight = (badgeVariant: BadgeVariant): { top: string | null; right: string | null } => {
  if (badgeVariant === 'standard') {
    return { top: '5px', right: '6px' };
  }
  return { top: null, right: null };
};

interface IUserProfileButtonProps {
  avatarUrl: string;
  placeHolderInitials: string;
  submenuOpen: boolean;
  badgeContent?: number;
  collapseOptions?: boolean;
  size?: number;
  badgeVariant?: BadgeVariant;
  handleClick(e: React.MouseEvent<HTMLElement>): void;
  handleKeyDown?(e: React.KeyboardEvent<HTMLElement>): void;
}
const UserProfileButtonBase: React.ForwardRefRenderFunction<HTMLButtonElement, IUserProfileButtonProps> = (
  {
    handleClick,
    handleKeyDown,
    avatarUrl,
    submenuOpen,
    collapseOptions,
    badgeContent,
    placeHolderInitials,
    size,
    badgeVariant
  },
  ref
) => {
  const displayBadge = collapseOptions && (badgeVariant || (badgeContent ?? 0) > 0);
  const avatarChild = (
    <Avatar
      alt="profile settings"
      src={avatarUrl}
      sx={{
        bgcolor: CAPITAL_FOUNDATION_COLORS.purple700,
        height: size ?? 24,
        width: size ?? 24,
        fontSize: '1rem'
      }}
    >
      {placeHolderInitials?.toUpperCase()}
    </Avatar>
  );
  const { top, right } = getTopAndRight(badgeVariant);
  const baseLabel = 'toggle profile menu';
  const badgeSuffix = displayBadge ? getBadgeAccessibilitySuffix(badgeContent, badgeVariant) : '';
  const ariaLabel = `${baseLabel}${badgeSuffix}`;

  return (
    <Tooltip title="My Profile" placement="top" enterNextDelay={500} disableInteractive={true} {...tooltipOffsetProps}>
      <IconButton
        ref={ref}
        sx={iconButtonStyles}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        aria-label={ariaLabel}
        aria-expanded={submenuOpen}
        aria-haspopup="true"
        id="profile-button"
        aria-controls="profile-menu"
        data-og={DATA_OG.profile_button}
        data-test={DATA_TEST.profile_button}
      >
        {displayBadge ? (
          <BadgeNotification top={top} right={right} badgeContent={badgeContent} badgeVariant={badgeVariant}>
            {avatarChild}
          </BadgeNotification>
        ) : (
          avatarChild
        )}
      </IconButton>
    </Tooltip>
  );
};

export const UserProfileButton = React.forwardRef(UserProfileButtonBase);

export const UserProfileDiv = ({
  avatarUrl,
  placeHolderInitials,
  size
}: Pick<IUserProfileButtonProps, 'placeHolderInitials' | 'avatarUrl' | 'size'>) => {
  return (
    <Avatar
      alt="profile settings"
      src={avatarUrl}
      data-og={DATA_OG.profile_div}
      data-test={DATA_TEST.profile_div}
      sx={{ bgcolor: CAPITAL_FOUNDATION_COLORS.purple600, height: size ?? 24, width: size ?? 24, fontSize: '1rem' }}
    >
      {placeHolderInitials?.toUpperCase()}
    </Avatar>
  );
};

export const ProfileSettingsOption = ({
  profileImgUrl,
  updateProfileUrl,
  updateProfileOnClick,
  updateProfileLinkComponent,
  placeHolderInitials,
  collapseOptions = false,
  badgeContent,
  handleSignOut,
  badgeVariant
}: IProfileSettingsOptions & { collapseOptions?: boolean; badgeContent?: number; badgeVariant?: BadgeVariant }) => {
  const profileButtonRef = React.useRef<HTMLButtonElement>(null);
  const firstTrayMenuItemRef = React.useRef<HTMLLIElement>(null);
  const [showProfileMenu, setShowProfileMenu] = React.useState(false);

  const canUpdateProfile = Boolean(updateProfileUrl || updateProfileOnClick);
  const canSignOut = Boolean(handleSignOut);

  const showButtonVariant = Boolean(collapseOptions || canUpdateProfile || canSignOut);

  if (updateProfileUrl && updateProfileOnClick) {
    console.error('Both updateProfileUrl and updateProfileOnClick are provided. Only one should be provided.');
  }

  return (
    <>
      <div data-test={DATA_TEST.profile_settings_option_container}>
        {showButtonVariant && (
          <UserProfileButton
            badgeContent={badgeContent}
            badgeVariant={badgeVariant}
            collapseOptions={collapseOptions}
            ref={profileButtonRef}
            avatarUrl={profileImgUrl || ''}
            submenuOpen={showProfileMenu}
            handleClick={() => {
              setShowProfileMenu(!showProfileMenu);
            }}
            handleKeyDown={(e) => {
              if (e.key === 'ArrowDown' && firstTrayMenuItemRef?.current) {
                firstTrayMenuItemRef.current.focus();
              }
            }}
            placeHolderInitials={placeHolderInitials || ''}
            size={32}
          />
        )}
        {!showButtonVariant && (
          <UserProfileDiv avatarUrl={profileImgUrl || ''} placeHolderInitials={placeHolderInitials || ''} size={32} />
        )}
      </div>
      <PopperSubmenuContainer
        disablePortal={true}
        open={showProfileMenu}
        anchorEl={profileButtonRef.current}
        handleClose={() => {
          delayedMobileCloseClickHandler(() => setShowProfileMenu(false));
        }}
        positionOffset={{ x: 0, y: 13 }}
        styleOverrides={{ width: collapseOptions ? '350px' : 'auto' }}
        dataTest={DATA_TEST.utility_tray_profile_submenu}
        placement="bottom-start"
      >
        {collapseOptions && (
          <MobileUtilityTray
            closeUtilityMenu={() => setShowProfileMenu(false)}
            firstTrayMenuItemRef={firstTrayMenuItemRef}
          />
        )}
        {!collapseOptions && (
          <MenuList id="profile-menu" aria-labelledby="profile-button" autoFocusItem={showProfileMenu}>
            {canUpdateProfile && (
              <TrayMenuItem
                firstTrayMenuItemRef={firstTrayMenuItemRef}
                url={updateProfileUrl || ''}
                label="My Profile"
                linkComponent={updateProfileLinkComponent}
                description="Update my profile information"
                dataTest={DATA_TEST.profile_settings_option}
                dataOg={DATA_OG.profile_settings_option}
                anchorEl={profileButtonRef?.current || undefined}
                onClick={() => {
                  if (updateProfileOnClick) {
                    updateProfileOnClick();
                  }
                }}
                handleClose={() => setShowProfileMenu(false)}
              />
            )}
            {canUpdateProfile && canSignOut && <Divider />}
            {canSignOut && (
              <TrayMenuItem
                firstTrayMenuItemRef={!updateProfileUrl && firstTrayMenuItemRef ? firstTrayMenuItemRef : undefined}
                dataTest={DATA_TEST.handle_sign_out}
                dataOg={DATA_OG.handle_sign_out}
                label="Log Out"
                onClick={handleSignOut}
                url=""
                anchorEl={profileButtonRef?.current || undefined}
                handleClose={() => setShowProfileMenu(false)}
              />
            )}
          </MenuList>
        )}
      </PopperSubmenuContainer>
    </>
  );
};
